import React, { forwardRef } from 'react';

import { Close } from '@lyearn/icons';
import { LinkButton } from '@lyearn/ui';

import useSearchState from '../Search/hooks/useSearchState';
import SearchInput from './SearchInput';
import type { SearchBarProps } from './types';

const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>((props, ref) => {
  const {
    value,
    startAdornment,
    endAdornment,
    onChange,
    onAfterClear,
    appearance,
    alwaysShowClearButton = false,
  } = props;
  const { searchText, setSearchText, debounceOnChange } = useSearchState({ value, onChange });
  const _onChange = React.useCallback(
    (searchValue: string) => {
      setSearchText(searchValue);
      debounceOnChange(searchValue);
    },
    [debounceOnChange, setSearchText],
  );

  const onClear = React.useCallback(() => {
    _onChange('');
    onAfterClear?.();
  }, [_onChange, onAfterClear]);

  const resolvedEndAdornment =
    value.length || alwaysShowClearButton
      ? endAdornment ?? (
          <LinkButton color={'internal'} onClick={onClear}>
            <Close className="icon-close fill-current text-icon-placeholder hover:text-icon-active" />
          </LinkButton>
        )
      : null;

  return (
    <SearchInput
      {...props}
      ref={ref}
      appearance={appearance}
      endAdornment={resolvedEndAdornment}
      startAdornment={startAdornment}
      value={searchText}
      onChange={_onChange}
    />
  );
});

export default React.memo(SearchBar);
